import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { useLocation } from "react-router-dom";
import { allPanels, type PanelKind, useDockStore } from "../../stores/dock";
import { FavoriteStar } from "../layout/FavoriteStar";
import { DOCK_KINDS, DOCK_KIND_CATALOG, type DockKindMap, KIND_ORDER } from "./registry";
import styles from "./DockRail.module.css";

/**
 * The dock rail (2026-07-15) — a button per dock KIND, always present, whether
 * or not anything of that kind is open. It is the launcher, so it lives OUTSIDE
 * the dock: a rail that only appears once the dock is up could never open it.
 *
 * "Every kind, even the empty ones" is the whole point. A launcher that only
 * lists what happens to be open is a tab bar, not a launcher — you could never
 * reach a kind you had closed. That is why it reads `DOCK_KIND_CATALOG` (every
 * kind the dock is being built toward) rather than the open panels, and why a
 * kind with no Body yet still gets a button: visibly not-built beats absent,
 * which is indistinguishable from never-planned.
 *
 * It replaces the lone chat toggle. Two unrelated top-right buttons is where the
 * mess starts, and the toggle's "close" was the reason the dock's empty state
 * had to grow a stopgap exit — that button is retired with this.
 */

interface DockRailProps {
	/** Injectable for tests. Production always uses the real registry. */
	kinds?: DockKindMap;
}

export function DockRail({ kinds = DOCK_KINDS }: DockRailProps) {
	const open = useDockStore((s) => s.open);
	const stacks = useDockStore((s) => s.stacks);
	const openDock = useDockStore((s) => s.openDock);
	const closeDock = useDockStore((s) => s.close);
	const setActive = useDockStore((s) => s.setActive);
	const openPanel = useDockStore((s) => s.openPanel);

	// Counts + focus reflect what THIS module's dock actually shows —
	// a badge counting another module's panels would be a number that lies.
	const moduleScope = useLocation().pathname.split("/")[1] || "dashboard";
	const panels = allPanels({ stacks }).filter((p) => !p.scope || p.scope === moduleScope);

	const activate = (kind: PanelKind) => {
		const def = kinds[kind];
		// A kind that CAN mint a panel gets a fresh one on every click — that is
		// what "new terminal" means, and focusing an existing shell instead would
		// be the wrong answer to the same gesture. Kinds that cannot be conjured
		// (an artifact arrives from the chat) focus their first open panel, or
		// just show the dock's empty state. This is the `create` seam named in
		// 1.4, now filled by the terminal — the first kind that can be made.
		if (def?.create) {
			openPanel(def.create());
			return;
		}
		const mine = panels.find((p) => p.kind === kind);
		if (mine) setActive(mine.id);
		openDock();
	};

	return (
		<div className={styles.rail}>
			{/* Star this page (owner, 2026-10-03). It rides here because this bar
			    is on every page — wherever the rail is drawn, the star is too —
			    but it is not a dock panel, so it sits outside the toolbar. */}
			<FavoriteStar className={styles.kindBtn} />
			<span className={styles.sep} aria-hidden="true" />
			<div className={styles.kinds} role="toolbar" aria-label="Dock panels">
			{KIND_ORDER.map((kind) => {
				const { label, railIcon } = DOCK_KIND_CATALOG[kind];
				const built = !!kinds[kind];
				const count = panels.filter((p) => p.kind === kind).length;
				return (
					<button
						key={kind}
						type="button"
						className={`${styles.kindBtn} ${count > 0 ? styles.kindActive : ""}`}
						onClick={() => activate(kind)}
						disabled={!built}
						// Says WHY it can't be clicked. A dead button with no explanation
						// reads as a bug; "not built yet" reads as a roadmap.
						title={
							built
								? kinds[kind]?.create
									? `New ${label.toLowerCase()}`
									: label
								: `${label} — not built yet`
						}
						aria-label={built ? label : `${label} (not built yet)`}
					>
						{railIcon}
						{/* Only when non-empty: the count is news, a "0" is noise. */}
						{count > 0 && <span className={styles.badge}>{count}</span>}
					</button>
				);
			})}

			<span className={styles.sep} aria-hidden="true" />

			<button
				type="button"
				className={styles.kindBtn}
				onClick={() => (open ? closeDock() : openDock())}
				// Hide/show, never discard — the panels come back with it.
				title={open ? "Hide dock" : "Show dock"}
				aria-label={open ? "Hide dock" : "Show dock"}
				aria-expanded={open}
			>
				{open ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
			</button>
			</div>
		</div>
	);
}
